import { useEffect, useMemo, useState } from 'react'
import { getDaysSinceColorClass } from 'utils/formatting'
import { format, differenceInDays } from 'date-fns'
import {
  PiCalendarBlank,
  PiCalendarStar,
  PiHandshake,
  PiEnvelopeOpen,
  PiNotepad,
  PiHandCoins,
  PiStack,
  PiListBullets,
  PiFileText,
} from 'react-icons/pi'
import { CompanyPageAccordion } from '../CompanyPageAccordion'
import { ResponsiveTabs, ResponsiveTabConfig } from '../../Tabs'
import { EventHistoryList } from './EventHistoryList'
import { DateRangePicker } from 'pages/app/deals/DateRangePicker'
import { SampleRequestPreviewModal } from '../../Samples/SampleRequestPreviewModal'
import { TeamMemberFilter } from './filters/TeamMemberFilter'
import { EmailsModal } from '../Modals/EmailsModal/EmailsModal'
import { ActivityV2 } from 'models/activityV2'
import { EmailMessage } from 'models/emailIntegration'
import { useEventHistoryFilterStore } from './EventHistoryFilterStore'
import { useEventHistoryQuery } from './useEventHistoryQuery'
import { buildEmailFromEvent } from './utils'
import { StageColorProvider } from 'components/SalesStage/StageColorContext'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'

export type EventHistoryTab =
  | 'all'
  | 'deals'
  | 'notes'
  | 'reminders'
  | 'samples'
  | 'emails'
  | 'documents'
  | 'account_attributes'

const EVENT_HISTORY_TABS: ResponsiveTabConfig<EventHistoryTab>[] = [
  { key: 'all', title: 'All', icon: PiListBullets },
  { key: 'deals', title: 'Deals', icon: PiHandshake },
  { key: 'notes', title: 'Notes', icon: PiNotepad },
  { key: 'reminders', title: 'Reminders', icon: PiCalendarStar },
  { key: 'samples', title: 'Samples', icon: PiHandCoins },
  { key: 'emails', title: 'Emails', icon: PiEnvelopeOpen },
  { key: 'documents', title: 'Documents', icon: PiFileText },
  { key: 'account_attributes', title: 'Account Attributes', icon: PiStack },
]

interface EventHistoryAccordionProps {
  chainProxyId: number
  doorProxyId?: number
  companyType?: string
}

export function EventHistoryAccordion({
  chainProxyId,
  doorProxyId,
}: EventHistoryAccordionProps) {
  const { dealDetailPage } = useFeatureFlagService()
  const [selectedTab, setSelectedTab] = useState<EventHistoryTab>('all')
  const [dateRange, setDateRange] = useState<
    | {
        from: Date
        to?: Date
      }
    | undefined
  >(undefined)
  // Reset shared filter store on mount to prevent cross-page filter leakage
  useEffect(() => {
    useEventHistoryFilterStore.getState().clearAllFilters()
  }, [chainProxyId])

  // Shared query hook — replaces inline useInfiniteQuery + buildQueryParams
  const {
    groupedEvents,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useEventHistoryQuery({
    chainProxyId,
    doorProxyId,
    selectedTab,
    dateRange,
  })

  // Sample preview modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [previewingSampleId, setPreviewingSampleId] = useState<number | null>(
    null
  )

  const handlePreviewRequest = (sampleId: number) => {
    setPreviewingSampleId(sampleId)
    setShowPreviewModal(true)
  }

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false)
    setPreviewingSampleId(null)
  }

  // Email view modal state
  const [selectedEmail, setSelectedEmail] = useState<EmailMessage | null>(null)

  const handleEmailClick = (event: ActivityV2) => {
    setSelectedEmail(buildEmailFromEvent(event))
  }

  // Header: most recent event + days badge
  const mostRecentEvent = useMemo(() => {
    if (groupedEvents.length === 0) return null
    return groupedEvents[0]?.events?.[0] ?? null
  }, [groupedEvents])

  const daysSinceUpdate = useMemo(() => {
    if (!mostRecentEvent?.created_at) return 0
    return differenceInDays(new Date(), new Date(mostRecentEvent.created_at))
  }, [mostRecentEvent])

  const headerRight = mostRecentEvent ? (
    <>
      <div className="flex items-center gap-1.5">
        <PiCalendarBlank className="w-5 h-5 text-fg-secondary" />
        <span
          className="text-sm font-light text-fg tabular-nums"
          style={{ lineHeight: '100%' }}
        >
          Updated {format(new Date(mostRecentEvent.created_at), 'MM/dd/yyyy')}
        </span>
      </div>

      <div className="flex h-6 items-center gap-1.5 rounded-full border border-line bg-surface-raised px-2.5 py-0.5">
        <div
          className={`h-2 w-2 rounded-full ${getDaysSinceColorClass(daysSinceUpdate)}`}
        />
        <span
          className="text-sm font-light text-fg tabular-nums"
          style={{ lineHeight: '100%' }}
        >
          <span className="font-semibold">{daysSinceUpdate}</span>{' '}
          {daysSinceUpdate === 1 ? 'day ago' : 'days ago'}
        </span>
      </div>
    </>
  ) : null

  return (
    <StageColorProvider value={dealDetailPage ? 'pipeline' : 'win_rate'}>
      <CompanyPageAccordion
        title="Event History"
        defaultOpen={true}
        stickyHeader={true}
        headerRight={headerRight}
        noPaddingBottom={true}
        bgClass="bg-white"
      >
        {/* Tabs and Search - static header */}
        <div className="bg-transparent space-y-4 pb-1 -mt-2">
          {/* Tabs */}
          <ResponsiveTabs
            tabs={EVENT_HISTORY_TABS}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            variant="pills"
          />

          {/* Filters Row — event-type filtering lives in the tabs above,
              so this row only needs the two value-based filters. */}
          <div className="w-full flex gap-2">
            <DateRangePicker value={dateRange} onChange={setDateRange} />
            <TeamMemberFilter inline />
          </div>
        </div>

        <EventHistoryList
          isLoading={isLoading}
          error={error}
          groupedEvents={groupedEvents}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          onPreviewRequest={handlePreviewRequest}
          onEmailClick={handleEmailClick}
        />

        {/* Sample Preview Modal */}
        {showPreviewModal && (
          <SampleRequestPreviewModal
            show={showPreviewModal}
            onHide={handleClosePreviewModal}
            sampleRequestId={previewingSampleId}
          />
        )}

        {/* Email View Modal */}
        {selectedEmail && (
          <EmailsModal
            show={!!selectedEmail}
            onHide={() => setSelectedEmail(null)}
            emailOnlyMode
            initialEmail={selectedEmail}
          />
        )}
      </CompanyPageAccordion>
    </StageColorProvider>
  )
}
